import type {PropertyValues} from 'lit';
import {html, LitElement} from 'lit';
import {localized, msg} from '@lit/localize';
import './element-address.ts';
import type {Addressable, FeedbackType} from '../apis/api-common.ts';
import {postFeedback} from '../apis/api-common.ts';
import {customElement, property, query, state} from 'lit/decorators.js';
import {divider} from '../helpers/elements.ts';
import './components/element-input.ts';
import './components/element-file-upload.ts';
import './components/element-line-checkbox.ts';
import './components/element-button.ts';
import {classMap} from 'lit/directives/class-map.js';
import type {ElementInput} from './components/element-input.ts';
import type {
  ElementFileUpload,
  UploadedEvent,
} from './components/element-file-upload.ts';
import {modal$, season$} from '../stores/store-main.ts';
import {isEmailValid} from '../helpers/string.ts';
import {WidgetInstance as CaptchaWidget} from 'friendly-challenge';
import {getLanguageCode} from '../i18n.ts';
import {CAPTCHA_SITEKEY} from '../envs.ts';
import {Router} from '@vaadin/router';
import {createSeasonURL} from '../urlHandling/urlCreate.ts';

const STYLE = {
  gridContainer: 'grid grid-cols-4 md:grid-cols-8 mdxx:grid-cols-12',
  gridElement:
    'col-span-full md:col-[1_/_span_8] mdxx:col-[1_/_span_8] xxl:col-[3_/_span_6]',
};

const ADDRESS: Addressable = {
  addressName: msg('SwitzerlandMobility Foundation'),
  addressName2: 'Monbijoustrasse 61 - 3007 Bern',
  addressMobile: '+41 31 313 02 70',
};

const FEEDBACK_OPTIONS: {type: FeedbackType; msg: string}[] = [
  {
    type: 'general',
    msg: msg(
      'Report general concerns, feedback or comments about signalisation',
    ),
  },
  {
    type: 'technic',
    msg: msg('Report a technical problem on the website or in the app'),
  },
  {
    type: 'beta',
    msg: msg('Submit feedback on the SwitzerlandMobility Plus beta version'),
  },
];

@localized()
@customElement('element-contact-form')
export class ElementContactForm extends LitElement {
  @property({type: Boolean}) overlayContent = false;
  @property() feedbackType: FeedbackType | undefined;
  @state() message = '';
  @state() secondName = '';
  @state() firstName = '';
  @state() email = '';
  @state() phoneNumber = '';
  @state() file: File | undefined;
  @state() sendActive = false;
  @state() captcha = '';
  @query('.answer-email-input') answerEmailInput: ElementInput;
  @query('element-file-upload') fileUpload: ElementFileUpload;
  private captchaWidget: CaptchaWidget;

  isShowBtnDisabled() {
    return (
      !this.captcha ||
      !this.feedbackType ||
      !this.message.length ||
      (this.feedbackType !== 'beta' &&
        (!this.firstName?.length ||
          !this.secondName?.length ||
          !isEmailValid(this.email)))
    );
  }

  cleanUpForm() {
    this.feedbackType = undefined;
    this.message = '';
    this.secondName = '';
    this.firstName = '';
    this.email = '';
    this.phoneNumber = '';
    this.fileUpload.deleteFile();
  }

  async send() {
    // iOS 17 mobile seems to ignore 'disabled' on button
    if (this.isShowBtnDisabled()) {
      return;
    }
    this.sendActive = true;
    const feedback = {
      captcha: this.captcha,
      feedbackType: this.feedbackType,
      message: this.message,
      firstName: this.firstName,
      secondName: this.secondName,
      email: this.email,
      file: this.file,
      phoneNumber: this.phoneNumber,
    };
    const response = await postFeedback(feedback);
    modal$.next(undefined);
    this.captchaWidget.reset();
    this.sendActive = false;
    if (response.ok) {
      if (!this.overlayContent)
        Router.go(createSeasonURL(getLanguageCode(), season$.value));
      const message =
        this.feedbackType === 'beta'
          ? msg('Thank you for your feedback.', {
              desc: 'snackbar text on feedback submission',
            })
          : msg('Your request has been sent.');
      this.dispatchEvent(
        new CustomEvent('alertnotif', {
          bubbles: true,
          detail: {
            type: 'success',
            message: message,
          },
        }),
      );
    } else {
      this.dispatchEvent(
        new CustomEvent('alertnotif', {
          bubbles: true,
          detail: {
            type: 'error',
            message: msg('Error: Request was not sent.'),
          },
        }),
      );
    }
    this.cleanUpForm();
  }

  cancel() {
    this.captchaWidget.reset();
    modal$.next(undefined);
    this.cleanUpForm();
  }

  override updated(changedProps: PropertyValues) {
    if (
      (changedProps.has('answer') || changedProps.has('copy')) &&
      this.email
    ) {
      this.answerEmailInput.validate();
    }
  }

  override connectedCallback() {
    super.connectedCallback();
    modal$.subscribe((options) => {
      if (options?.type === 'contact' && options.data) {
        this.feedbackType = options.data.feedbackType;
      }
    });
  }

  override render() {
    const isBetaFeedback = this.feedbackType === 'beta';
    return html` <div
      class="${classMap({
        'px-details-xxs': this.overlayContent,
        'sm:px-details-m': this.overlayContent,
      })} grid grid-cols-4 pb-details-s md:grid-cols-8 md:pb-details-l mdxx:grid-cols-12"
    >
      <div class="${STYLE.gridElement}">
        <h1 data-cy="place-title" class="h1 mb-4">${msg('Contact form')}</h1>
      </div>
      <div class="${STYLE.gridElement}">
        <element-address
          .item="${ADDRESS}"
          .bigOverlay="${true}"
        ></element-address>
        <div class="body flex flex-col gap-y-[14px] pt-[14px]">
          <p>
            ${msg(
              'We are continuing to enhance SwitzerlandMobility. Your opinion and feedback about our routes, the signalisation, our website or the app are most welcome. Please use our contact form to send us your comments.',
            )}
          </p>
          <p>
            ${msg(
              'You can also use the contact form to report technical errors concerning the website or the app. We will take care of the problem as quickly as possible.',
            )}
          </p>
          <p>${msg('Thank you in advance for your support!')}</p>
          <p>${msg('Your SwitzerlandMobility Foundation')}</p>
        </div>
        <div class="body flex flex-col gap-y-[14px] pt-[32px]">
          <div class="font-bold">${msg('Select a topic')}</div>
          ${FEEDBACK_OPTIONS.map(
            (opt) => html`
              <div class="flex">
                <input
                  .checked="${this.feedbackType === opt.type}"
                  id="${`radio-feedback-${opt.type}`}"
                  type="radio"
                  value="${opt.type}"
                  name="${opt.type}"
                  class="mt-[2px] h-radio w-radio md:mt-[5px]"
                  @change="${() => (this.feedbackType = opt.type)}"
                />
                <label
                  for="${`radio-feedback-${opt.type}`}"
                  class="ml-[12px] select-none"
                  >${opt.msg}</label
                >
              </div>
            `,
          )}
        </div>
      </div>
      <div .hidden="${!this.feedbackType}" class="col-span-full">
        ${divider('100%', '2px', '32px 0', '#495057')}
        <div class="${STYLE.gridContainer} gap-y-[24px]">
          <element-input
            class="${STYLE.gridElement}"
            .value="${this.message}"
            placeholder="${msg('Message')}"
            label=${msg('Message')}
            .textArea="${true}"
            .required="${true}"
            @input="${(evt: InputEvent) => {
              const target = <HTMLInputElement>evt.target;
              this.message = target.value;
            }}"
          >
          </element-input>
          <element-file-upload
            class="col-[1_/_span_4] xxl:col-[3_/_span_4]"
            label="${msg('Upload file or image')}"
            hint="${msg('Drag or click to add file or photo, max. 10 MB')}"
            @uploaded="${(evt: UploadedEvent) => (this.file = evt.detail.file)}"
            @deleted="${() => {
              this.file = undefined;
            }}"
          >
          </element-file-upload>
        </div>
      </div>
      <div .hidden="${!this.feedbackType}" class="col-span-full">
        ${divider('100%', '2px', '32px 0', '#495057')}
        <div class="${STYLE.gridContainer}">
          <div class="${STYLE.gridElement} body flex flex-col gap-y-[32px]">
            <div class="flex flex-col gap-y-[32px]">
              <div class="flex flex-col gap-y-[8px]">
                <div class="font-bold">
                  ${msg('Your contact information')}
                  ${isBetaFeedback ? `(${msg('optional')})` : ''}
                </div>
                <div>
                  ${isBetaFeedback
                    ? msg(
                        'We very much appreciate your feedback on the beta version of SwitzerlandMobility Plus. Thank you for your understanding that we are unable to respond to all feedback messages.',
                      )
                    : msg('We need your information in order to contact you.')}
                </div>
              </div>
              <div class="flex flex-col gap-y-[24px]">
                <div
                  class="flex flex-col gap-x-[16px] gap-y-[24px] sm:flex-row"
                >
                  <element-input
                    class="flex-1"
                    label="${msg('Name')}"
                    .required="${!isBetaFeedback}"
                    .value="${this.secondName}"
                    placeholder="${msg('Name')}"
                    @input="${(evt: InputEvent) =>
                      (this.secondName = (<HTMLInputElement>evt.target).value)}"
                  >
                  </element-input>
                  <element-input
                    class="flex-1"
                    label="${msg('First name')}"
                    .required="${!isBetaFeedback}"
                    .value="${this.firstName}"
                    placeholder="${msg('First name')}"
                    @input="${(evt: InputEvent) =>
                      (this.firstName = (<HTMLInputElement>evt.target).value)}"
                  >
                  </element-input>
                </div>
                <div
                  class="flex flex-col gap-x-[16px] gap-y-[24px] sm:flex-row"
                >
                  <element-input
                    class="answer-email-input flex-1"
                    label="${msg('E-mail address')}"
                    .required="${!isBetaFeedback}"
                    .type="${'email'}"
                    .value="${this.email}"
                    placeholder="${msg('E-mail address')}"
                    @input="${(evt: InputEvent) =>
                      (this.email = (<HTMLInputElement>evt.target).value)}"
                  >
                  </element-input>
                  <element-input
                    class="flex-1"
                    label="${msg('Phone number')}"
                    .type="${'tel'}"
                    .value="${this.phoneNumber}"
                    placeholder="${msg('Phone number')}"
                    @input="${(evt: InputEvent) =>
                      (this.phoneNumber = (<HTMLInputElement>(
                        evt.target
                      )).value)}"
                  >
                  </element-input>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="col-span-full pt-[32px] md:col-[1_/_span_8] mdxx:col-[1_/_span_8] xxl:col-[3_/_span_6]"
      >
        <div class="frc-captcha h-logo-s sm:h-logo-m"></div>
      </div>
      <div class="col-span-full mt-[40px] flex justify-end gap-x-[16px]">
        <element-button
          .type=${'secondary'}
          .label="${() => msg('Cancel')}"
          .hidden="${!this.overlayContent}"
          @click="${this.cancel}"
        ></element-button>
        <div
          class="${classMap({'cursor-not-allowed': this.isShowBtnDisabled()})}"
        >
          <element-button
            .disabled=${this.isShowBtnDisabled()}
            .loading="${this.sendActive}"
            .label="${() => msg('Send')}"
            @click="${this.send}"
          ></element-button>
        </div>
      </div>
    </div>`;
  }

  override firstUpdated() {
    this.captchaWidget = new CaptchaWidget(this.querySelector('.frc-captcha'), {
      startMode: 'auto',
      sitekey: CAPTCHA_SITEKEY,
      language: getLanguageCode(),
      doneCallback: (token: string) => (this.captcha = token),
    });
    this.captchaWidget.start();
  }

  override createRenderRoot() {
    return this;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'element-contact-form': ElementContactForm;
  }
}
